import React, { memo } from "react";
import { Handle } from "react-flow-renderer";
import { AgentIcon, CopyIcon, TrashIcon } from "../icons/FlowIcons";
import { useNodeStorage } from "../../../stores/useNodeStorage";
import { TargetHandle, glassStyle, OptionOutputHandle, optionRowStyle, TimeoutOutputHandle } from "./NodeHandles";

const featureBadge = (label) => (
  <span style={{
    fontSize: "8px",
    color: "var(--flow-text-secondary)",
    backgroundColor: "var(--flow-node-inner-bg)",
    border: "1px solid var(--flow-node-inner-border)",
    borderRadius: 6,
    padding: "2px 6px",
    marginRight: 3,
    fontWeight: 600,
  }}>
    {label}
  </span>
);

export default memo(({ data, isConnectable, id }) => {
  const storageItems = useNodeStorage();

  const config = data.typebotIntegration || {};
  const routingOptions = config.routingOptions || [];
  const fields = config.fields || [];
  const enableRouting = config.enableRouting || false;
  const enableFields = config.enableFields || false;
  const enableTransfer = config.enableTransfer || false;
  const enableRAG = config.enableRAG || false;
  const enableMemory = config.enableMemory || false;
  const bufferSeconds = Number(config.messageBufferSeconds) || 0;

  return (
    <div style={{ ...glassStyle, width: 220 }} className="flow-node-card">
      <TargetHandle isConnectable={isConnectable} />
      <div
        className="node-actions"
        style={{
          display: "flex",
          position: "absolute",
          right: 5,
          top: 5,
          cursor: "pointer",
          gap: 6
        }}
      >
        <CopyIcon
          onClick={() => { storageItems.setNodesStorage(id); storageItems.setAct("duplicate"); }}
          style={{ width: "12px", height: "12px", color: "var(--flow-text-muted)" }}
        />
        <TrashIcon
          onClick={() => { storageItems.setNodesStorage(id); storageItems.setAct("delete"); }}
          style={{ width: "12px", height: "12px", color: "var(--flow-text-muted)" }}
        />
      </div>

      {/* Header */}
      <div style={{ color: "var(--flow-text)", fontSize: "14px", display: "flex", alignItems: "center", marginBottom: 4, fontWeight: 700 }}>
        <span style={{ marginRight: 6, display: "flex" }}><AgentIcon size={18} style={{ color: "var(--flow-icon-color)" }} /></span>
        {config.name || "Agente IA"}
      </div>

      {/* Nome do agente (assinatura) */}
      {config.agentDisplayName && (
        <div style={{ fontSize: "9px", color: "var(--flow-text-muted)", marginBottom: 4, fontStyle: "italic" }}>
          Assinatura: {config.agentDisplayName}
        </div>
      )}

      {/* Badges das funcionalidades ativas */}
      <div style={{ marginBottom: 6, display: "flex", flexWrap: "wrap" }}>
        {enableRAG && featureBadge("RAG")}
        {enableMemory && featureBadge("Memória")}
        {enableRouting && featureBadge("Routing")}
        {enableFields && featureBadge("Coleta")}
        {enableTransfer && featureBadge("Transfer")}
        {!enableRouting && !enableFields && !enableTransfer && !enableRAG && !enableMemory && featureBadge("Chat")}
        {bufferSeconds >= 5 && featureBadge(`Buffer ${bufferSeconds}s`)}
      </div>

      {/* Campos sendo coletados */}
      {enableFields && fields.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          {fields.slice(0, 3).map(field => (
            <div
              key={field.name}
              style={{
                fontSize: "9px",
                color: "var(--flow-text-muted)",
                padding: "1px 6px",
                backgroundColor: "var(--flow-node-inner-bg)",
                borderRadius: 3,
                marginBottom: 1,
              }}
            >
              {field.label || field.name} {field.required ? "●" : ""}
            </div>
          ))}
          {fields.length > 3 && (
            <div style={{ fontSize: "8px", color: "var(--flow-text-faint)", textAlign: "center" }}>
              +{fields.length - 3} campos
            </div>
          )}
        </div>
      )}

      {/* Saídas de ROUTING (cada setor = uma saída) */}
      {enableRouting && routingOptions.length > 0 && routingOptions.map((option) => (
        <div key={option} style={optionRowStyle}>
          <div style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontSize: "10px",
            color: "var(--flow-text-secondary)",
            fontWeight: 600,
          }}>
            {option}
          </div>
          <OptionOutputHandle isConnectable={isConnectable} id={"route_" + option} />
        </div>
      ))}

      {/* Saída de TRANSFERÊNCIA */}
      {enableTransfer && (
        <div style={optionRowStyle}>
          <div style={{ fontSize: "10px", color: "var(--flow-text-secondary)", fontWeight: 600 }}>
            Transferir
          </div>
          <OptionOutputHandle isConnectable={isConnectable} id="transfer" />
        </div>
      )}

      {/* Saída padrão (quando não tem routing) */}
      {!enableRouting && !enableTransfer && (
        <div style={optionRowStyle}>
          <div style={{ fontSize: "10px", color: "var(--flow-text-secondary)" }}>Saída</div>
          <OptionOutputHandle isConnectable={isConnectable} id="a" />
        </div>
      )}

      {/* Erro */}
      <div style={optionRowStyle}>
        <div style={{ fontSize: "10px", color: "var(--flow-text-secondary)", fontWeight: 600 }}>Erro</div>
        <Handle
          type="source"
          position="right"
          id="error"
          style={{
            position: "absolute", right: "-13px", top: "50%", transform: "translateY(-50%)",
            width: "7px", height: "7px", cursor: "pointer",
            background: "var(--flow-handle-color)", border: "1px solid var(--flow-handle-color)", boxShadow: "0 0 4px var(--flow-node-shadow)",
          }}
          isConnectable={isConnectable}
        />
      </div>

      {/* Timeout */}
      <TimeoutOutputHandle
        isConnectable={isConnectable}
        label={data.timeoutSeconds > 0 ? `Timeout ${data.timeoutSeconds}s` : "Sem resposta"}
      />
    </div>
  );
});

